/**
 * Copyright (c) 2026 Accunai India Services Pvt. Ltd. All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This file is part of proprietary software owned by Accunai India Services Pvt. Ltd.
 * Unauthorized copying, distribution, modification, or use of this file,
 * in whole or in part, via any medium, is strictly prohibited without
 * prior written permission from Accunai India Services Pvt. Ltd.
 *
 * Generated with GitHub Copilot Business/Enterprise.
 * Reviewed and approved by: AI-assisted / 2026-08-13
 */

import axios, { AxiosRequestConfig } from "axios";
import { constants } from "../constants/env";
import {
	clearAuthCookies,
	getAuthTokenCookie,
	getRefreshTokenCookie,
	setAuthTokenCookie,
	setRefreshTokenCookie,
} from "../shared/auth/cookieStore";
import { isAuthFailureStatusCode } from "../shared/errors/normalizeApiError";

type RefreshErrorCode =
	| "REFRESH_INVALID"
	| "REFRESH_EXPIRED"
	| "REFRESH_REUSED"
	| "REFRESH_REVOKED";

interface RefreshResponse {
	"authentication-token"?: string;
	"refresh-token"?: string;
	access_expires_in?: number;
	refresh_expires_in?: number;
}

interface RequestConfigWithRetry extends AxiosRequestConfig {
	_retry?: boolean;
	_skipAuthRefresh?: boolean;
}

const REFRESH_PATH = "auth/token/refresh/";
const LOGIN_PATH = "auth/company/user/login/other/";
const LOGOUT_PATH = "auth/company/user/logout/other/";
const ACCESS_TOKEN_REFRESH_BUFFER_SECONDS = 60;
const TERMINAL_REFRESH_ERROR_CODES: RefreshErrorCode[] = [
	"REFRESH_INVALID",
	"REFRESH_EXPIRED",
	"REFRESH_REUSED",
	"REFRESH_REVOKED",
];

let activeRefreshPromise: Promise<string | null> | null = null;

const buildApiUrl = (path: string): string => {
	const baseUrl = constants.API_BASE_URL.endsWith("/")
		? constants.API_BASE_URL
		: `${constants.API_BASE_URL}/`;

	return new URL(path, baseUrl).toString();
};

const redirectToLogin = (): void => {
	if (window.location.pathname !== "/new/login") {
		window.location.href = "/new/login";
	}
};

const shouldSkipRefreshForUrl = (url?: string): boolean => {
	if (!url) {
		return false;
	}

	return url.includes(REFRESH_PATH) || url.includes(LOGOUT_PATH) || url.includes(LOGIN_PATH);
};

const clearSessionAndRedirect = (): void => {
	clearAuthCookies();
	localStorage.removeItem("authenticated");
	redirectToLogin();
};

const parseJwtExpiryEpochSeconds = (token: string): number | null => {
	try {
		const encodedPayload = token.split(".")[1];
		if (!encodedPayload) {
			return null;
		}

		const base64Payload = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
		const payload = JSON.parse(atob(base64Payload)) as { exp?: number };
		return typeof payload.exp === "number" ? payload.exp : null;
	} catch {
		return null;
	}
};

const shouldRefreshBeforeExpiry = (token: string): boolean => {
	const expiryEpochSeconds = parseJwtExpiryEpochSeconds(token);
	if (!expiryEpochSeconds) {
		return false;
	}

	const currentEpochSeconds = Math.floor(Date.now() / 1000);
	return expiryEpochSeconds - currentEpochSeconds <= ACCESS_TOKEN_REFRESH_BUFFER_SECONDS;
};

const isExplicitTokenExpiryResponse = (error: any): boolean => {
	const responseBody = error?.response?.data;
	const responseMessage =
		typeof responseBody === "string"
			? responseBody
			: responseBody?.message ?? responseBody?.detail ?? responseBody?.error;

	return (
		typeof responseMessage === "string" &&
		/\b(token|jwt)\b.*\b(expired|invalid)\b/i.test(responseMessage)
	);
};

const refreshAccessToken = async (): Promise<string | null> => {
	const refreshToken = getRefreshTokenCookie();
	if (!refreshToken) {
		clearSessionAndRedirect();
		return null;
	}

	try {
		const response = await httpClient.post<RefreshResponse>(
			buildApiUrl(REFRESH_PATH),
			{ refresh_token: refreshToken },
			{ _skipAuthRefresh: true } as RequestConfigWithRetry
		);

		const refreshResponse = response.data as RefreshResponse;
		const newAccessToken = refreshResponse["authentication-token"];
		const newRefreshToken = refreshResponse["refresh-token"];

		if (!newAccessToken || !newRefreshToken) {
			clearSessionAndRedirect();
			return null;
		}

		setAuthTokenCookie(newAccessToken, 1);
		setRefreshTokenCookie(newRefreshToken, 14);

		return newAccessToken;
	} catch (error: any) {
		const statusCode: number | undefined = error?.response?.status;
		const errorCode: RefreshErrorCode | undefined = error?.response?.data?.error_code;

		if (
			statusCode === 401 ||
			statusCode === 403 ||
			(errorCode !== undefined && TERMINAL_REFRESH_ERROR_CODES.includes(errorCode))
		) {
			clearSessionAndRedirect();
		}

		return null;
	}
};

export const httpClient = axios.create({
	baseURL: buildApiUrl(""),
	timeout: 15000,
});

httpClient.interceptors.request.use(async (config) => {
	const mutableConfig = config as typeof config & RequestConfigWithRetry;
	const requestData = mutableConfig.data;

	// Let the browser set multipart boundaries for FormData uploads.
	if (typeof FormData !== "undefined" && requestData instanceof FormData) {
		const requestHeaders = axios.AxiosHeaders.from(mutableConfig.headers);
		requestHeaders.delete("Content-Type");
		requestHeaders.delete("content-type");
		mutableConfig.headers = requestHeaders;
	}

	if (!mutableConfig._skipAuthRefresh) {
		let authToken = getAuthTokenCookie();
		if (authToken && shouldRefreshBeforeExpiry(authToken)) {
			if (!activeRefreshPromise) {
				activeRefreshPromise = refreshAccessToken().finally(() => {
					activeRefreshPromise = null;
				});
			}

			const refreshedAccessToken = await activeRefreshPromise;
			if (refreshedAccessToken) {
				authToken = refreshedAccessToken;
			}
		}

		if (authToken) {
			mutableConfig.headers = (mutableConfig.headers ?? {}) as any;
			mutableConfig.headers.Authorization = `Bearer ${authToken}`;
		}
	}

	return mutableConfig;
});

httpClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		const statusCode: number | undefined = error?.response?.status;
		const originalRequest = (error?.config ?? {}) as RequestConfigWithRetry & {
			url?: string;
			headers?: Record<string, string>;
		};

		const isAuthFailure =
			isAuthFailureStatusCode(statusCode) || isExplicitTokenExpiryResponse(error);

		if (
			!isAuthFailure ||
			originalRequest._skipAuthRefresh ||
			originalRequest._retry ||
			shouldSkipRefreshForUrl(originalRequest.url)
		) {
			return Promise.reject(error);
		}

		if (!activeRefreshPromise) {
			activeRefreshPromise = refreshAccessToken().finally(() => {
				activeRefreshPromise = null;
			});
		}

		const refreshedAccessToken = await activeRefreshPromise;
		if (!refreshedAccessToken) {
			return Promise.reject(error);
		}

		originalRequest._retry = true;
		originalRequest.headers = originalRequest.headers ?? {};
		originalRequest.headers.Authorization = `Bearer ${refreshedAccessToken}`;

		return httpClient(originalRequest);
	}
);
